import {Chip, ChipProps} from '@mui/material';
import {semantic, neutral} from 'src/theme';

type BadgeVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

const variantStyles: Record<BadgeVariant, {bgcolor: string; color: string}> = {
  success: {bgcolor: semantic.success.light, color: semantic.success.dark},
  error: {bgcolor: semantic.error.light, color: semantic.error.dark},
  warning: {bgcolor: semantic.warning.light, color: semantic.warning.dark},
  info: {bgcolor: semantic.info.light, color: semantic.info.dark},
  neutral: {bgcolor: neutral[100], color: neutral[600]},
};

interface StatusBadgeProps extends Omit<ChipProps, 'variant' | 'color'> {
  /** Semantic variant that controls background and text color */
  status: BadgeVariant;
}

/**
 * Unified status badge for all entity states.
 *
 * Replaces: StatusChip, inline Chip with ad-hoc sx, and all hardcoded
 * success/error/warning chip patterns across the codebase.
 *
 * Usage:
 *   <StatusBadge status="success" label="Online" />
 *   <StatusBadge status="error" label="Failed" />
 *   <StatusBadge status="warning" label="Pending" />
 */
export default function StatusBadge({status, label, sx, ...props}: StatusBadgeProps) {
  const style = variantStyles[status];

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: style.bgcolor,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.6875rem',
        letterSpacing: '0.02em',
        borderRadius: 1,
        height: 22,
        ...sx,
      }}
      {...props}
    />
  );
}

/**
 * Helpers to map common domain statuses to badge variants.
 * Use these to ensure consistent mapping across all pages.
 */
export function getDeviceStatusVariant(status?: string): BadgeVariant {
  switch (status?.toUpperCase()) {
    case 'ONLINE':
    case 'DEVICE_STATUS_ONLINE':
      return 'success';
    case 'OFFLINE':
    case 'DEVICE_STATUS_OFFLINE':
      return 'error';
    default:
      return 'neutral';
  }
}

export function getEventStatusVariant(status?: string): BadgeVariant {
  switch (status?.toLowerCase()) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
}

export function getOccupancyStatusVariant(status?: string): BadgeVariant {
  switch (status?.toUpperCase()) {
    case 'OCCUPIED':
      return 'success';
    case 'VACANT':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function getTenantStatusVariant(status?: string): BadgeVariant {
  const s = status?.toLowerCase();
  switch (s) {
    case 'active':
    case 'enabled':
      return 'success';
    case 'pending':
    case 'invited':
      return 'warning';
    case 'created':
      return 'info';
    case 'inactive':
    case 'expired':
      return 'error';
    default:
      return 'neutral';
  }
}

export function getDeliveryStatusVariant(status?: string): BadgeVariant {
  switch (status?.toLowerCase()) {
    case 'delivered':
    case 'picked_up':
      return 'success';
    case 'pending':
    case 'waiting':
      return 'warning';
    case 'expired':
    case 'cancelled':
      return 'error';
    default:
      return 'neutral';
  }
}
